/** Shared customer form validation (name / email / phone).
 *  Keep in sync with backend/validation.py.
 */

/* -------------------------------------------------------------------------- */
/* Normalization                                                              */
/* -------------------------------------------------------------------------- */

function normalize(value: string): string {
  return value.normalize("NFKC").trim();
}

/* -------------------------------------------------------------------------- */
/* Name                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Supports Unicode letters, spaces, hyphens, apostrophes, periods.
 *
 * Examples:
 *   John Smith
 *   Mary-Jane O'Connor
 *   José García
 *   Dr. John Smith
 *   李明
 *
 * Rejects:
 *   John123
 *   ---
 *   ...
 *   J
 */

const REPEATED_NAME_SEP_RE = /[.]{2}|'{2}|-{2}| {2}/;
const NAME_SEP = new Set([" ", ".", "'", "-"]);

function isLetter(ch: string): boolean {
  return /\p{L}/u.test(ch);
}

function isMark(ch: string): boolean {
  return /\p{M}/u.test(ch);
}

export function isValidPersonName(
  value: string,
  { required = true } = {},
): boolean {
  const name = normalize(value);

  if (!name) return !required;
  if (name.length < 2 || name.length > 80) return false;
  if (/\p{N}/u.test(name)) return false;

  const letters = name.match(/\p{L}/gu) || [];
  if (letters.length < 2) return false;

  if (!isLetter(name[0]) || !isLetter(name[name.length - 1])) return false;
  if (REPEATED_NAME_SEP_RE.test(name)) return false;

  for (const ch of name) {
    if (isLetter(ch) || isMark(ch) || NAME_SEP.has(ch)) continue;
    return false;
  }
  return true;
}

/* -------------------------------------------------------------------------- */
/* Email                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Practical application-level email validation (not full RFC).
 */

const EMAIL_RE =
  /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?)+$/i;

export function isValidEmail(
  value: string,
  { required = true } = {},
): boolean {
  const email = normalize(value);

  if (!email) return !required;
  if (email.length > 254) return false;
  if (/\s/.test(email)) return false;

  const atIndex = email.indexOf("@");
  if (atIndex === -1 || atIndex !== email.lastIndexOf("@")) return false;

  const [local, domain] = email.split("@");
  if (!local || local.length > 64) return false;
  if (!domain || domain.length > 253) return false;
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) {
    return false;
  }
  if (!domain.includes(".")) return false;

  return EMAIL_RE.test(email);
}

/* -------------------------------------------------------------------------- */
/* Phone                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Practical phone-number validation.
 *
 * Frontend purpose:
 *   - Validate user input format.
 *   - Reject obviously invalid/fake values.
 *   - Do NOT attempt to reproduce every country's numbering rules.
 *
 * Accepted examples:
 *   4155551234
 *   415-555-1234
 *   (415) 555-1234
 *   +1 415 555 1234
 *   +44 20 7946 0958
 *   +66 81 234 5678
 *
 * Rejected examples:
 *   123456789
 *   1234567890123456
 *   1111111111
 *   +++14155551234
 *   415--555-1234
 *   abc4155551234
 *
 * For authoritative international validation, validate again on the backend
 * using a real phone-number library such as libphonenumber.
 */

const PHONE_ALLOWED_RE = /^[\d\s+().-]+$/;

function phoneDigits(value: string): string {
  return value.replace(/\D/g, "");
}

export function phoneDigitCount(value: string): number {
  return phoneDigits(normalize(value)).length;
}

/** Reject numbers made entirely from the same digit (000… / 111…). */
function isRepeatedDigitPhone(digits: string): boolean {
  return /^(\d)\1+$/.test(digits);
}

/**
 * Reject obviously sequential fake numbers
 * (0123456789 / 1234567890 / 9876543210), including wrap 9→0 / 0→9.
 */
function isSequentialPhone(digits: string): boolean {
  if (digits.length < 10) return false;

  let ascending = true;
  let descending = true;
  for (let i = 1; i < digits.length; i++) {
    const prev = Number(digits[i - 1]);
    const cur = Number(digits[i]);
    if (cur !== (prev + 1) % 10) ascending = false;
    if (cur !== (prev + 9) % 10) descending = false;
    if (!ascending && !descending) return false;
  }
  return ascending || descending;
}

/**
 * Parentheses must be balanced and only one nesting level.
 * Valid: (415) 555-1234
 */
function hasValidParentheses(phone: string): boolean {
  let depth = 0;
  for (const ch of phone) {
    if (ch === "(") {
      depth++;
      if (depth > 1) return false;
    } else if (ch === ")") {
      depth--;
      if (depth < 0) return false;
    }
  }
  return depth === 0;
}

/** Reject 415--555 / 415..555 / 415  555 style repeats. */
function hasRepeatedSeparators(phone: string): boolean {
  return /([ .-])\1+/.test(phone);
}

/**
 * Accepts common international phone formats.
 * Requires 10–15 digits when non-empty.
 * Default required=false so optional phone fields on other forms keep working.
 */
export function isValidPhone(
  value: string,
  { required = false } = {},
): boolean {
  const phone = normalize(value);

  if (!phone) return !required;
  if (!PHONE_ALLOWED_RE.test(phone)) return false;

  const digits = phoneDigits(phone);

  // International E.164 maximum is 15 digits.
  if (digits.length < 10 || digits.length > 15) return false;

  // "+" may occur only once and only at the beginning.
  const plusCount = (phone.match(/\+/g) || []).length;
  if (plusCount > 1) return false;
  if (plusCount === 1 && !phone.startsWith("+")) return false;

  if (!hasValidParentheses(phone)) return false;
  if (hasRepeatedSeparators(phone)) return false;
  if (isRepeatedDigitPhone(digits)) return false;
  if (isSequentialPhone(digits)) return false;

  // Prefer +E.164 over 00 international prefix when no leading +.
  if (!phone.startsWith("+") && /^00/.test(digits)) return false;

  return true;
}

/**
 * Auto-format phone as the user types.
 *
 * US / NANP:
 *   5551234567     → (555) 123-4567
 *   15551234567    → +1 (555) 123-4567
 *   +15551234567   → +1 (555) 123-4567
 *
 * International (leading + or more than 11 digits):
 *   +442079460958  → +44 207 946 0958
 *   +66812345678   → +66 812 345 678
 */
export function formatPhoneInput(raw: string): string {
  const trimmed = raw.trimStart();
  const wantsPlus = trimmed.startsWith("+");
  let digits = trimmed.replace(/\D/g, "");
  if (digits.length > 15) digits = digits.slice(0, 15);
  if (!digits) return wantsPlus ? "+" : "";

  // US: up to 10 digits, or 11 starting with country code 1.
  const isNanp =
    (!wantsPlus && digits.length <= 10) ||
    (!wantsPlus && digits.length === 11 && digits.startsWith("1")) ||
    (wantsPlus && digits.startsWith("1") && digits.length <= 11);

  if (isNanp) {
    const withCountry =
      wantsPlus || (digits.length === 11 && digits.startsWith("1"));
    const national =
      digits.startsWith("1") && (wantsPlus || digits.length === 11)
        ? digits.slice(1, 11)
        : digits.slice(0, 10);
    return formatUsNational(national, withCountry);
  }

  // Longer domestic paste without +: treat as international.
  return formatInternational(digits);
}

function formatUsNational(national: string, withCountry: boolean): string {
  const d = national.slice(0, 10);
  const prefix = withCountry ? "+1 " : "";
  if (d.length === 0) return withCountry ? "+1" : "";
  if (d.length < 4) return `${prefix}(${d}`;
  if (d.length < 7) return `${prefix}(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `${prefix}(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatInternational(digits: string): string {
  // Country code: 1 for NANP leftovers, otherwise 2 (most common).
  let ccLen = 2;
  if (digits.startsWith("1")) ccLen = 1;
  else if (digits.length <= 4) ccLen = Math.min(2, digits.length);

  const cc = digits.slice(0, ccLen);
  let rest = digits.slice(ccLen);

  // Stable national patterns when complete (or near-complete).
  if (rest.length === 10) {
    return `+${cc} ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  }
  if (rest.length === 9) {
    return `+${cc} ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  }
  if (rest.length === 8) {
    return `+${cc} ${rest.slice(0, 4)} ${rest.slice(4)}`;
  }

  // Progressive while typing: groups of 3, leave remainder ≤4 together.
  const groups: string[] = [];
  while (rest.length > 4) {
    groups.push(rest.slice(0, 3));
    rest = rest.slice(3);
  }
  if (rest) groups.push(rest);
  return `+${[cc, ...groups].filter(Boolean).join(" ")}`;
}

/* -------------------------------------------------------------------------- */
/* Messages                                                                   */
/* -------------------------------------------------------------------------- */

export function requiredMessage(label: string): string {
  return `${label} is required.`;
}

export function nameMessage(label = "Name"): string {
  return `Please enter a valid ${label.toLowerCase()} (2–80 letters; numbers are not allowed).`;
}

export function emailMessage(label = "Email"): string {
  return `Please enter a valid ${label.toLowerCase()} (example: you@company.com).`;
}

export function phoneMessage(label = "Phone"): string {
  return `Please enter a valid ${label.toLowerCase()} with 10–15 digits (example: (555) 123-4567).`;
}

/* -------------------------------------------------------------------------- */
/* Combined validation                                                        */
/* -------------------------------------------------------------------------- */

export interface CustomerFormInput {
  name: string;
  email: string;
  phone: string;
}

export interface CustomerFormErrors {
  name?: string;
  email?: string;
  phone?: string;
}

export function validateCustomerForm(
  input: CustomerFormInput,
): CustomerFormErrors {
  const errors: CustomerFormErrors = {};

  const name = normalize(input.name);
  const email = normalize(input.email);
  const phone = normalize(input.phone);

  if (!name) {
    errors.name = requiredMessage("Name");
  } else if (!isValidPersonName(name)) {
    errors.name = nameMessage();
  }

  if (!email) {
    errors.email = requiredMessage("Email");
  } else if (!isValidEmail(email)) {
    errors.email = emailMessage();
  }

  if (!phone) {
    errors.phone = requiredMessage("Phone");
  } else if (!isValidPhone(phone, { required: true })) {
    errors.phone = phoneMessage();
  }

  return errors;
}

export function isValidCustomerForm(input: CustomerFormInput): boolean {
  return Object.keys(validateCustomerForm(input)).length === 0;
}
